import * as VisuallyHidden from "@radix-ui/react-visually-hidden";
import { type PropsWithChildren, Suspense, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useSnapshot } from "valtio";
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerTitle,
} from "../../../../../components/drawer";
import { LoadingSpinner } from "../../../../../components/loading";
import {
  useGetClient,
  useGetLead,
  useGetProfile,
} from "../../../../../services";
import { homeState } from "../../../../../state/home";
import { LinkCreatorDrawerContent } from "./LinkCreatorDrawerContent";
import { LinkCreatorHeader } from "./LinkCreatorHeader";
import { useGetOrganizationProfileSkills } from "../../../../../services/organtization/skills/get-organization-profile-skills";
import TagsInputDialog from "../../../../../components/dialogs/TagsInputDialog";
import { ProfileSkillTag } from "../../../../../types";
import { SkillTag } from "../../../../../client";
import { useResetLink } from "../../../../../services/link/reset-link";

export const LinkCreatorDrawer = ({ children }: PropsWithChildren) => {
  const { organizationId } = useParams();
  if (!organizationId) throw new Error("Organization ID is required");

  const [addingTagsForWorkplace, setAddingTagsForWorkplace] =
    useState<boolean>(false);
  const [addingTagsForProfileSummary, setAddingTagsForProfileSummary] =
    useState<boolean>(false);

  const [isDefaultLead, setIsDefaultLead] = useState<boolean>(false);

  const [showTagDialog, setShowTagDialog] = useState<boolean>(false);
  const [selectedSkillTags, setSelectedSkillTags] = useState<ProfileSkillTag[]>(
    [],
  );
  const { linkCreatorLeadId, linkCreatorIsDefaultLead } =
    useSnapshot(homeState);
  const [skillsAddedFromDialog, setSkillsAddedFromDialog] = useState<
    SkillTag[]
  >([]);
  const [
    profileSummarySkillsAddedFromDialog,
    setProfileSummarySkillsAddedFromDialog,
  ] = useState<SkillTag[]>([]);

  const { data: lead } = useGetLead(
    {
      leadID: linkCreatorLeadId ?? "",
      organizationID: organizationId,
    },
    { enabled: !!linkCreatorLeadId },
  );

  const { data: parentLead } = useGetLead(
    {
      leadID: lead?.parentLeadId ?? "",
      organizationID: organizationId,
    },
    { enabled: !!lead?.parentLeadId },
  );

  // Child leads in grouped mode don't carry parsedAssignmentData — it lives on the parent.
  // Merge it in so downstream checks (e.g. hasAssignmentData) see the correct state.
  const effectiveLead =
    lead && lead.parentLeadId && parentLead
      ? {
          ...lead,
          parsedAssignmentData:
            lead.parsedAssignmentData ?? parentLead.parsedAssignmentData,
        }
      : lead;

  const { data: profile } = useGetProfile(
    {
      organizationId,
      profileId: lead?.profileId ?? "",
    },
    {
      enabled: !!lead?.profileId,
    },
  );

  const { data: client } = useGetClient(
    {
      organizationID: organizationId,
      clientId: lead?.clientId ?? "",
    },
    {
      enabled: !!lead?.clientId,
    },
  );

  const { mutate: resetLink, variables: linkVariables } = useResetLink();

  const handleResetLink = () => {
    if (lead && lead.profileLinkId) {
      resetLink({
        path: { profileLink: lead.profileLinkId },
      });
    }
  };

  const handleLinkCreatorClose = () => {
    // TODO: Save link
    homeState.linkCreatorLeadId = null;
    homeState.linkCreatorIsDefaultLead = null;
  };

  return (
    <Drawer
      direction="right"
      open={!!linkCreatorLeadId}
      onClose={handleLinkCreatorClose}
      shouldScaleBackground={false}
    >
      {children}

      <DrawerContent
        className="min-w-[80%] max-w-[80%]"
        direction="right"
        data-vaul-no-drag
      >
        <VisuallyHidden.Root>
          <DrawerTitle>Edit link</DrawerTitle>
          <DrawerDescription>
            Edit the link for the Lead Profile
          </DrawerDescription>
        </VisuallyHidden.Root>
        {effectiveLead && (
          <>
            <LinkCreatorHeader
              profile={profile}
              client={client}
              title={
                linkCreatorIsDefaultLead
                  ? "Edit Default Profile"
                  : "Edit unique Profile"
              }
              handleClose={handleLinkCreatorClose}
              handleReset={handleResetLink}
            />
            <Suspense fallback={<Loading />}>
              <LinkCreatorDrawerContent
                lead={effectiveLead}
                organizationId={organizationId}
                onShowAddSkillsDialog={(
                  currentSkillTags: SkillTag[],
                  addingTagsForProfileSummary: boolean,
                ) => {
                  setShowTagDialog(true);
                  setAddingTagsForProfileSummary(addingTagsForProfileSummary);
                  setAddingTagsForWorkplace(!addingTagsForProfileSummary);
                  setSelectedSkillTags(
                    currentSkillTags.map((skillTag) => {
                      let ps: ProfileSkillTag = {
                        name: skillTag.name,
                        customIconName: skillTag.customIconName,
                      };
                      return ps;
                    }),
                  );
                }}
                skillsAddedFromDialog={skillsAddedFromDialog}
                profileSummarySkillsAddedFromDialog={
                  profileSummarySkillsAddedFromDialog
                }
                onAddedCustomSkills={() => {
                  setProfileSummarySkillsAddedFromDialog([]);
                  setSkillsAddedFromDialog([]);
                }}
                isDefaultLead={linkCreatorIsDefaultLead ?? false}
              />
            </Suspense>
          </>
        )}
        {showTagDialog ? (
          <TagsInputDialog
            open={true}
            onBackdropClick={() => setShowTagDialog(false)}
            darkModeActive={false}
            onCancel={() => {
              setShowTagDialog(false);
              setAddingTagsForProfileSummary(false);
              setAddingTagsForWorkplace(false);
            }}
            onSave={() => {}}
            onSkillAdded={(skillAdded: ProfileSkillTag) => {
              if (
                selectedSkillTags.filter((tag) => tag.name === skillAdded.name)
                  .length > 0
              ) {
                return;
              }

              if (addingTagsForProfileSummary) {
                setProfileSummarySkillsAddedFromDialog([
                  ...skillsAddedFromDialog,
                  {
                    name: skillAdded.name,
                    customIconName: skillAdded.customIconName,
                  },
                ]);
              } else if (addingTagsForWorkplace) {
                setSkillsAddedFromDialog([
                  ...skillsAddedFromDialog,
                  {
                    name: skillAdded.name,
                    customIconName: skillAdded.customIconName,
                  },
                ]);
              }
            }}
            selectedProfileSkillTags={[]}
            onClose={() => {
              setShowTagDialog(false);
              setAddingTagsForProfileSummary(false);
              setAddingTagsForWorkplace(false);
            }}
          />
        ) : (
          <div />
        )}
      </DrawerContent>
    </Drawer>
  );
};

const Loading = () => (
  <div className="flex justify-center items-center h-full w-full">
    <LoadingSpinner size="small" />
  </div>
);
